import { motion } from 'framer-motion';
import { 
  FileText, Clock, CheckCircle, ArrowRight, XCircle, 
  Send, PenTool, Zap, FileCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problems = [
  "Passer des heures a rediger des devis",
  "Chercher des descriptions techniques",
  "Copier-coller des textes d'anciens devis",
  "Faire des erreurs de calcul",
];

const features = [
  { icon: PenTool, title: "Descriptifs metiers prets", text: "Les textes techniques sont deja rediges pour toi" },
  { icon: Zap, title: "Modification rapide", text: "Modifiez simplement les prix et quantites" },
  { icon: Send, title: "Envoi en PDF", text: "Envoyez tes devis par email en un clic" },
  { icon: FileCheck, title: "Signature en ligne", text: "Tes clients signent directement sur leur telephone" },
];

const benefits = [
  "Devis envoyes en quelques minutes",
  "Moins d'erreurs de saisie",
  "Transformation automatique en facture",
  "Historique des devis conserve",
];

export default function DevisIntellPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Devis Intelligents | Crée tes Devis en Quelques Clics - BatiAssist"
        description="Créez des devis professionnels en quelques minutes avec BatiAssist : descriptifs métiers pré-remplis, calculs automatiques, signature électronique et transformation en facture."
        keywords="devis bâtiment, devis artisan automatique, logiciel devis BTP, signature électronique devis, facturation artisan"
        path="/fonctionnalites/devis-intelligents"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <FileText className="w-4 h-4" />
              Devis Intelligents
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Creez tes devis en{' '}
              <span className="text-ba-secondary">quelques secondes</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-6 leading-relaxed">
              Les descriptifs métiers sont déjà prêts. Tu dois simplement <strong>modifier tes prix</strong>.
            </p>
            <div className="flex items-center justify-center gap-2 text-ba-success mb-10">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Temps moyen : 3 minutes par devis</span>
            </div>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                Creer un devis en quelques secondes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold uppercase rounded-full mb-4">
                Le probleme
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Les artisans perdent des heures sur leurs devis
              </h2>
              <p className="text-lg text-slate-600">
                Le soir, apres une journee de chantier, les artisans doivent encore passer du temps a rediger des devis. 
                Resultat : fatigue, erreurs et clients qui attendent.
              </p>
            </div>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-100"
                >
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700">{problem}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Des devis prets en quelques clics
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              BatiAssist integre des descriptifs deja prepares pour les principaux metiers du batiment.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-ba-secondary/30 hover:shadow-lg transition-all text-center"
                >
                  <div className="w-14 h-14 bg-ba-secondary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Icon className="w-7 h-7 text-ba-secondary" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Du devis a la facture en un clic
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Une fois le devis accepte, transformez-le automatiquement en facture. 
                Plus besoin de tout ressaisir.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ba-primary rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <span className="text-slate-700">Creez ton devis avec les descriptifs prets</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ba-primary rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <span className="text-slate-700">Envoyez en PDF par email</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ba-primary rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <span className="text-slate-700">Le client signe en ligne</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ba-success rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                  <span className="text-slate-700">Transformation automatique en facture</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-ba-secondary to-orange-600 rounded-3xl p-8"
            >
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-slate-800">Devis #2024-0042</h3>
                  <span className="px-3 py-1 bg-ba-success/10 text-ba-success text-sm rounded-full">Accepte</span>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Renovation salle de bain</span>
                    <span className="font-medium">4 500 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Plomberie complete</span>
                    <span className="font-medium">2 200 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Carrelage</span>
                    <span className="font-medium">1 800 €</span>
                  </div>
                </div>
                <div className="border-t pt-4 flex justify-between font-bold">
                  <span>Total TTC</span>
                  <span className="text-ba-primary">8 500 €</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Les avantages des devis intelligents
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200"
              >
                <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                <span className="text-slate-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-ba-secondary">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Arretez de perdre du temps sur tes devis
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Creez des devis professionnels en quelques minutes, pas en quelques heures.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-ba-secondary font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Creer un devis en quelques secondes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
